<template>
  <section
    ref="moduleRootRef"
    class="pipeline-with-filters-module-component">
    <div class="pipeline-container">
      <div
        v-if="data.title"
        class="title-section">
        <h2
          class="title-section-heading"
          v-html="data.title"></h2>
      </div>

      <div class="pipeline">
        <div class="filters">
          <div class="filter-trigger">
            <div class="filter-header">
              <p>Filters</p>
              <button
                v-if="hasActiveFilters"
                type="button"
                class="clear-all"
                @click="clearAllFilters">
                Clear All
              </button>
            </div>
            <button
              v-if="device === 'desktop'"
              class="hidden opacity-0 pointer-events-none lg:pointer-events-auto lg:opacity-100 lg:flex transition-transform duration-300"
              :class="{
                'rotate-180': !filtersOpen,
              }"
              @click="filtersOpen = !filtersOpen">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="8"
                viewBox="0 0 15 8"
                fill="none">
                <path
                  d="M7.5 2.43224C7.27941 2.43224 7.2479 2.46154 6.9958 2.72528L1.32353 8L0.882354 8L5.16736e-07 7.17949L4.8087e-07 6.76923L7.27941 4.45017e-07L7.72059 4.06448e-07L15 6.73993L15 7.15018L14.1176 7.9707L13.6765 7.9707L8.0042 2.69597C7.7521 2.46154 7.68908 2.40293 7.5 2.40293L7.5 2.43224Z"
                  fill="#13D377" />
              </svg>
            </button>
          </div>

          <component
            :is="isDesktop ? Vue3SlideUpDown : 'div'"
            :key="filtersPanelKey"
            v-bind="
              isDesktop
                ? {
                    modelValue: isFiltersPanelOpen,
                    responsive: true,
                    'onUpdate:modelValue': (value) => {
                      isFiltersPanelOpen = value
                    },
                  }
                : {}
            ">
            <div class="filters-dropdown">
              <div
                v-if="diseaseAreasFilters.length"
                class="filter-field">
                <button
                  type="button"
                  class="filter-mobile-trigger"
                  :class="{ open: isFilterFieldOpen('diseaseAreas') }"
                  @click="toggleFilterField('diseaseAreas')">
                  <span class="filter-label">Disease Areas</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="8"
                    viewBox="0 0 15 8"
                    fill="none">
                    <path
                      d="M7.5 2.43224C7.27941 2.43224 7.2479 2.46154 6.9958 2.72528L1.32353 8L0.882354 8L5.16736e-07 7.17949L4.8087e-07 6.76923L7.27941 4.45017e-07L7.72059 4.06448e-07L15 6.73993L15 7.15018L14.1176 7.9707L13.6765 7.9707L8.0042 2.69597C7.7521 2.46154 7.68908 2.40293 7.5 2.40293L7.5 2.43224Z"
                      fill="#13D377" />
                  </svg>
                </button>

                <Vue3SlideUpDown
                  :model-value="isFilterFieldOpen('diseaseAreas')">
                  <div class="button-list">
                    <button
                      v-for="option in diseaseAreasFilters"
                      :key="`disease-areas-${option}`"
                      type="button"
                      class="filter-button"
                      :class="{ active: selectedDiseaseAreas.includes(option) }"
                      @click="onMultiFilterClick(selectedDiseaseAreas, option)">
                      {{ option }}
                    </button>
                  </div>
                </Vue3SlideUpDown>
              </div>

              <div
                v-if="clinicalPhaseFilters.length"
                class="filter-field">
                <button
                  type="button"
                  class="filter-mobile-trigger"
                  :class="{ open: isFilterFieldOpen('clinicalPhase') }"
                  @click="toggleFilterField('clinicalPhase')">
                  <span class="filter-label">Clinical Phase</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="8"
                    viewBox="0 0 15 8"
                    fill="none">
                    <path
                      d="M7.5 2.43224C7.27941 2.43224 7.2479 2.46154 6.9958 2.72528L1.32353 8L0.882354 8L5.16736e-07 7.17949L4.8087e-07 6.76923L7.27941 4.45017e-07L7.72059 4.06448e-07L15 6.73993L15 7.15018L14.1176 7.9707L13.6765 7.9707L8.0042 2.69597C7.7521 2.46154 7.68908 2.40293 7.5 2.40293L7.5 2.43224Z"
                      fill="#13D377" />
                  </svg>
                </button>

                <Vue3SlideUpDown
                  :model-value="isFilterFieldOpen('clinicalPhase')">
                  <div class="button-list">
                    <button
                      v-for="option in clinicalPhaseFilters"
                      :key="`clinical-phase-${option}`"
                      type="button"
                      class="filter-button"
                      :class="{
                        active: selectedClinicalPhases.includes(option),
                      }"
                      @click="
                        onMultiFilterClick(selectedClinicalPhases, option)
                      ">
                      {{ option }}
                    </button>
                  </div>
                </Vue3SlideUpDown>
              </div>

              <div
                v-if="treatmentApproachFilters.length"
                class="filter-field">
                <button
                  type="button"
                  class="filter-mobile-trigger"
                  :class="{ open: isFilterFieldOpen('treatmentApproach') }"
                  @click="toggleFilterField('treatmentApproach')">
                  <span class="filter-label">Treatment Approach</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="8"
                    viewBox="0 0 15 8"
                    fill="none">
                    <path
                      d="M7.5 2.43224C7.27941 2.43224 7.2479 2.46154 6.9958 2.72528L1.32353 8L0.882354 8L5.16736e-07 7.17949L4.8087e-07 6.76923L7.27941 4.45017e-07L7.72059 4.06448e-07L15 6.73993L15 7.15018L14.1176 7.9707L13.6765 7.9707L8.0042 2.69597C7.7521 2.46154 7.68908 2.40293 7.5 2.40293L7.5 2.43224Z"
                      fill="#13D377" />
                  </svg>
                </button>

                <Vue3SlideUpDown
                  :model-value="isFilterFieldOpen('treatmentApproach')">
                  <div class="button-list">
                    <button
                      v-for="option in treatmentApproachFilters"
                      :key="`treatment-approach-${option}`"
                      type="button"
                      class="filter-button"
                      :class="{
                        active: selectedTreatmentApproaches.includes(option),
                      }"
                      @click="
                        onMultiFilterClick(selectedTreatmentApproaches, option)
                      ">
                      {{ option }}
                    </button>
                  </div>
                </Vue3SlideUpDown>
              </div>

              <div
                v-if="moleculeFilters.length"
                class="filter-field molecule-filter">
                <button
                  type="button"
                  class="filter-mobile-trigger"
                  :class="{ open: isFilterFieldOpen('molecule') }"
                  @click="toggleFilterField('molecule')">
                  <span class="filter-label">Molecule</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="8"
                    viewBox="0 0 15 8"
                    fill="none">
                    <path
                      d="M7.5 2.43224C7.27941 2.43224 7.2479 2.46154 6.9958 2.72528L1.32353 8L0.882354 8L5.16736e-07 7.17949L4.8087e-07 6.76923L7.27941 4.45017e-07L7.72059 4.06448e-07L15 6.73993L15 7.15018L14.1176 7.9707L13.6765 7.9707L8.0042 2.69597C7.7521 2.46154 7.68908 2.40293 7.5 2.40293L7.5 2.43224Z"
                      fill="#13D377" />
                  </svg>
                </button>

                <Vue3SlideUpDown :model-value="isFilterFieldOpen('molecule')">
                  <div class="button-list">
                    <button
                      v-for="option in moleculeFilters"
                      :key="`molecule-${option}`"
                      type="button"
                      class="filter-button"
                      :class="{ active: selectedMolecules.includes(option) }"
                      @click="onMultiFilterClick(selectedMolecules, option)"
                      v-html="option"></button>
                  </div>
                </Vue3SlideUpDown>
              </div>
            </div>
          </component>
        </div>

        <div
          v-if="isFiltering"
          class="pipeline-group skeleton-group">
          <h3 class="group-title skeleton-title"></h3>
          <div class="pipeline-items">
            <div
              v-for="index in 3"
              :key="`skeleton-item-${index}`"
              class="item skeleton-item">
              <div class="inner-item">
                <div class="front-card">
                  <div class="top">
                    <div class="skeleton-line skeleton-line-lg"></div>
                    <div class="skeleton-line skeleton-line-md"></div>
                    <div class="skeleton-line skeleton-line-sm"></div>
                  </div>
                  <div class="bottom">
                    <div class="skeleton-pill"></div>
                    <div class="skeleton-circle"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <template v-else-if="groupedPipelineItems.length">
          <div
            v-for="(group, groupIndex) in groupedPipelineItems"
            :key="`pipeline-group-${group.title}-${groupIndex}`"
            class="pipeline-group"
            :class="{
              pair: groupIndex % 2 === 0,
            }">
            <div class="group-header">
              <h3
                class="group-title"
                v-html="group.title"></h3>

              <button
                v-if="isMobile"
                :id="`pipeline-mobile-group-trigger-${groupIndex}`"
                type="button"
                class="group-mobile-trigger"
                :class="{ open: isPipelineGroupOpen(groupIndex) }"
                @click="togglePipelineGroup(groupIndex)">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="8"
                  viewBox="0 0 15 8"
                  fill="none">
                  <path
                    d="M7.5 2.43224C7.27941 2.43224 7.2479 2.46154 6.9958 2.72528L1.32353 8L0.882354 8L5.16736e-07 7.17949L4.8087e-07 6.76923L7.27941 4.45017e-07L7.72059 4.06448e-07L15 6.73993L15 7.15018L14.1176 7.9707L13.6765 7.9707L8.0042 2.69597C7.7521 2.46154 7.68908 2.40293 7.5 2.40293L7.5 2.43224Z"
                    fill="#13D377" />
                </svg>
              </button>
            </div>

            <component
              :is="isDesktop ? 'div' : Vue3SlideUpDown"
              v-bind="
                isDesktop ? {} : { modelValue: isPipelineGroupOpen(groupIndex) }
              ">
              <div class="pipeline-items">
                <div
                  v-for="entry in group.items"
                  :key="`pipeline-${entry.flatIndex}-item`"
                  ref="cardRefs"
                  :data-flat-index="entry.flatIndex"
                  class="item"
                  :class="{ active: isCardActive(entry.flatIndex) }">
                  <div class="inner-item">
                    <!-- Front card information -->
                    <div class="front-card">
                      <div class="top">
                        <div class="inner-top">
                          <div class="title-type">
                            <div class="subtitle-logo">
                              <p
                                class="title"
                                v-html="
                                  getCardTitleDisplay(
                                    entry.pipeline.clinical_trial
                                      .front_card_information.title
                                  )
                                "></p>
                              <p
                                v-if="
                                  entry.pipeline.clinical_trial
                                    .front_card_information.subtitle
                                "
                                class="small-title subtitle"
                                v-html="
                                  entry.pipeline.clinical_trial
                                    .front_card_information.subtitle
                                "></p>
                            </div>

                            <div class="subtitle-logo down">
                              <div
                                class="focus"
                                :style="{
                                  backgroundColor:
                                    entry.pipeline.clinical_trial
                                      .front_card_information
                                      .focus_area_background,
                                }">
                                <p
                                  v-if="getCardTagName(entry.pipeline)"
                                  :style="{
                                    color:
                                      entry.pipeline.clinical_trial
                                        .front_card_information
                                        .focus_area_text_color,
                                  }"
                                  v-html="getCardTagName(entry.pipeline)"></p>
                              </div>

                              <img
                                v-if="
                                  entry.pipeline.clinical_trial
                                    .front_card_information.add_logo
                                "
                                :src="
                                  entry.pipeline.clinical_trial
                                    .front_card_information.logo.url
                                "
                                :alt="
                                  entry.pipeline.clinical_trial
                                    .front_card_information.logo.alt
                                " />
                            </div>
                          </div>
                        </div>

                        <div
                          v-if="
                            entry.pipeline.clinical_trial.front_card_information
                              .details
                          "
                          class="details">
                          <p class="small-title details-title">Details</p>

                          <div
                            class="details-content"
                            v-html="
                              entry.pipeline.clinical_trial
                                .front_card_information.details
                            "></div>
                        </div>
                      </div>

                      <div class="bottom z-3">
                        <div class="flex flex-row items-end gap-3">
                          <div
                            class="flex flex-col gap-1 w-full max-w-[150px] flex-[1_0_auto] lg:w-[150px]">
                            <span
                              class="clinical-phase-value"
                              v-html="
                                getClinicalPhaseCardLabel(
                                  entry.pipeline.clinical_trial
                                    .front_card_information.clinical_phase
                                )
                              "></span>
                            <div
                              v-if="
                                entry.pipeline.clinical_trial
                                  .front_card_information.clinical_phase
                              "
                              class="title-tag"
                              :class="{
                                'is-complete':
                                  getProgressBarValue(entry.pipeline) === 100,
                              }">
                              <div
                                class="background-tag"
                                :style="
                                  getProgressBarStyle(entry.pipeline)
                                "></div>
                            </div>
                          </div>
                          <p
                            v-if="
                              entry.pipeline.clinical_trial
                                .front_card_information.phase_label
                            "
                            class="phase-label"
                            v-html="
                              entry.pipeline.clinical_trial
                                .front_card_information.phase_label
                            "></p>
                        </div>

                        <a
                          v-if="
                            entry.pipeline.clinical_trial.add_more_details &&
                            entry.pipeline.clinical_trial.details.add_button
                          "
                          class="pipeline-button"
                          :href="
                            entry.pipeline.clinical_trial.details.button.url
                          "
                          :target="
                            entry.pipeline.clinical_trial.details.button.target
                          "
                          :title="
                            entry.pipeline.clinical_trial.details.button.title
                          "
                          v-html="
                            entry.pipeline.clinical_trial.details.button.title
                          "></a>

                        <button
                          v-if="entry.pipeline.clinical_trial.add_more_details"
                          type="button"
                          class="trigger"
                          @click="toggleCardActive(entry.flatIndex)">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="8"
                            viewBox="0 0 15 8"
                            fill="none">
                            <path
                              d="M7.5 5.56777C7.72059 5.56777 7.7521 5.53846 8.0042 5.27473L13.6765 0H14.1176L15 0.820513V1.23077L7.72059 8H7.27941L0 1.26007V0.849817L0.882353 0.029304H1.32353L6.9958 5.30403C7.2479 5.53846 7.31092 5.59707 7.5 5.59707V5.56777Z"
                              fill="#0E4343" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <!-- end of Front card information -->

                    <!-- Reversed card information -->

                    <div
                      v-if="entry.pipeline.clinical_trial.add_more_details"
                      class="back-card">
                      <div
                        class="details-content"
                        v-html="
                          entry.pipeline.clinical_trial.details.content
                        "></div>
                      <div
                        v-if="entry.pipeline.clinical_trial.details.disclaimer"
                        class="disclaimer"
                        v-html="
                          entry.pipeline.clinical_trial.details.disclaimer
                        "></div>

                      <a
                        v-if="
                          entry.pipeline.clinical_trial.add_more_details &&
                          entry.pipeline.clinical_trial.details.add_button
                        "
                        class="opacity-0 pointer-events-none mt-6"
                        :href="entry.pipeline.clinical_trial.details.button.url"
                        :target="
                          entry.pipeline.clinical_trial.details.button.target
                        "
                        :title="
                          entry.pipeline.clinical_trial.details.button.title
                        "
                        v-html="
                          entry.pipeline.clinical_trial.details.button.title
                        "></a>
                    </div>
                  </div>
                </div>
              </div>
            </component>

            <div
              v-if="getPhaseDisclaimer(group.title)"
              class="phase-disclaimer"
              v-html="getPhaseDisclaimer(group.title)"></div>
          </div>
        </template>

        <div
          v-else
          class="no-results">
          <p>No results match the selected filters.</p>
        </div>

        <div
          v-if="data.disclaimer_text"
          class="disclaimer-div"
          v-html="data.disclaimer_text"></div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "PipelineWithFiltersComponent",
    "CLASSNAME": "pipeline-with-filters-module-component",
    "URL": "http://vuevitewp.local/test"
   }
  #ENDMETA */

/*
 *   Insert your logic here
 */
import useSmoothScroll from '@/components/composables/useSmoothScroll'
import useUtils from '@/components/composables/useUtils'
import { useStore } from '@/stores/main'
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import Vue3SlideUpDown from 'vue3-slide-up-down'

const props = defineProps(['data'])
const store = useStore()
const acf_options = ref(null)
const moduleRootRef = ref(null)
const cardRefs = ref([])
const activeCards = ref({})
let layoutRefreshFrame = null
let windowLoadHandler = null
const diseaseAreasFilters = ref([])
const clinicalPhaseFilters = ref([])
const clinicalPhaseOrder = ref([])
const clinicalPhaseShortLabels = ref({})
const treatmentApproachFilters = ref([])
const moleculeFilters = ref([])
const selectedDiseaseAreas = ref([])
const selectedClinicalPhases = ref([])
const selectedTreatmentApproaches = ref([])
const selectedMolecules = ref([])
const mobilePipelineGroupsOpen = ref({})
const filtersOpen = ref(false)
const isFiltering = ref(false)
const mobileFilterFieldsOpen = ref({
  diseaseAreas: false,
  clinicalPhase: false,
  treatmentApproach: false,
  molecule: false,
})
const { device } = useUtils()
const { locoScroll } = useSmoothScroll()

const FILTER_LOADING_MS = 1200
let filterLoadingTimer = null

const isDesktop = computed(() => device.value === 'desktop')
const isMobile = computed(() => !isDesktop.value)
const hasActiveFilters = computed(
  () =>
    selectedDiseaseAreas.value.length > 0 ||
    selectedClinicalPhases.value.length > 0 ||
    selectedTreatmentApproaches.value.length > 0 ||
    selectedMolecules.value.length > 0
)

const isFiltersPanelOpen = computed({
  get: () => filtersOpen.value,
  set: (value) => {
    filtersOpen.value = value
  },
})

const filtersPanelKey = computed(() =>
  [
    device.value,
    diseaseAreasFilters.value.length,
    clinicalPhaseFilters.value.length,
    treatmentApproachFilters.value.length,
    moleculeFilters.value.length,
  ].join('-')
)

const isFilterFieldOpen = (fieldKey) =>
  !isMobile.value || Boolean(mobileFilterFieldsOpen.value[fieldKey])

const isPipelineGroupOpen = (groupIndex) =>
  isDesktop.value || Boolean(mobilePipelineGroupsOpen.value[groupIndex])

const toggleFilterField = (fieldKey) => {
  if (!isMobile.value) return

  mobileFilterFieldsOpen.value = {
    ...mobileFilterFieldsOpen.value,
    [fieldKey]: !mobileFilterFieldsOpen.value[fieldKey],
  }
}

const syncMobilePipelineGroupsOpen = () => {
  if (isDesktop.value) return

  const nextState = {}

  groupedPipelineItems.value.forEach((_, groupIndex) => {
    nextState[groupIndex] = Object.prototype.hasOwnProperty.call(
      mobilePipelineGroupsOpen.value,
      groupIndex
    )
      ? mobilePipelineGroupsOpen.value[groupIndex]
      : false
  })

  mobilePipelineGroupsOpen.value = nextState
}

const togglePipelineGroup = async (groupIndex) => {
  if (isDesktop.value) return

  const triggerId = `pipeline-mobile-group-trigger-${groupIndex}`
  const triggerSelector = `#${triggerId}`

  mobilePipelineGroupsOpen.value = {
    ...mobilePipelineGroupsOpen.value,
    [groupIndex]: !mobilePipelineGroupsOpen.value[groupIndex],
  }

  await setCardHeights()

  if (locoScroll.value?.scrollTo) {
    locoScroll.value.scrollTo(triggerSelector, {
      immediate: false,
      duration: 1,
    })
    return
  }

  const triggerElement = document.getElementById(triggerId)
  if (!triggerElement) return

  triggerElement.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  })
}

const startFilterLoading = () => {
  locoScroll.value?.stop?.()
  isFiltering.value = true

  if (filterLoadingTimer) {
    clearTimeout(filterLoadingTimer)
  }

  filterLoadingTimer = setTimeout(() => {
    isFiltering.value = false
    filterLoadingTimer = null
  }, FILTER_LOADING_MS)
}

const toggleMultiFilter = (filterRef, option) => {
  const target = Array.isArray(filterRef)
    ? filterRef
    : Array.isArray(filterRef?.value)
    ? filterRef.value
    : null

  if (!target) return

  const optionIndex = target.indexOf(option)

  if (optionIndex === -1) {
    target.push(option)
    return
  }

  target.splice(optionIndex, 1)
}

const onMultiFilterClick = (filterRef, option) => {
  toggleMultiFilter(filterRef, option)
  startFilterLoading()
}

const clearAllFilters = () => {
  selectedDiseaseAreas.value = []
  selectedClinicalPhases.value = []
  selectedTreatmentApproaches.value = []
  selectedMolecules.value = []
  startFilterLoading()
}

const getValue = (obj, snakeKey, camelKey) => {
  if (!obj) return undefined
  if (Object.prototype.hasOwnProperty.call(obj, snakeKey)) return obj[snakeKey]
  return obj[camelKey]
}

const mapOptionList = (items, optionKeySnake, optionKeyCamel) => {
  if (!Array.isArray(items)) return []

  return items
    .map((item) => getValue(item, optionKeySnake, optionKeyCamel))
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean)
}

const stripHtml = (value) => String(value ?? '').replace(/<[^>]*>/g, '')

const normalizeComparableValue = (value) =>
  stripHtml(value).trim().toLowerCase()

const extractComparableValues = (value) => {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => extractComparableValues(item))
      .filter(Boolean)
  }

  if (typeof value === 'object' && value !== null) {
    const objectValue = getValue(value, 'value', 'value')
    const objectLabel = getValue(value, 'label', 'label')
    return extractComparableValues(objectValue ?? objectLabel)
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => normalizeComparableValue(item))
      .filter(Boolean)
  }

  const normalized = normalizeComparableValue(value)
  return normalized ? [normalized] : []
}

const matchesSelectedFilters = (selectedValues, itemValue) => {
  if (!selectedValues.length) return true

  const selectedComparable = selectedValues.map(normalizeComparableValue)
  const itemComparable = extractComparableValues(itemValue)

  return selectedComparable.some((selected) =>
    itemComparable.includes(selected)
  )
}

const hydratePipelineFilters = () => {
  const pipelineFilters =
    getValue(acf_options.value, 'pipeline_filters', 'pipelineFilters') || {}

  const diseaseAreasConfig = getValue(
    pipelineFilters,
    'disease_areas_filters',
    'diseaseAreasFilters'
  )
  const showDiseaseAreas = getValue(
    pipelineFilters,
    'show_disease_areas_filter',
    'showDiseaseAreasFilter'
  )
  diseaseAreasFilters.value = showDiseaseAreas
    ? mapOptionList(
        getValue(
          diseaseAreasConfig,
          'disease_areas_filter_options',
          'diseaseAreasFilterOptions'
        ),
        'disease_areas_filter_option',
        'diseaseAreasFilterOption'
      )
    : []

  const clinicalPhaseConfig = getValue(
    pipelineFilters,
    'clinical_phase_filters',
    'clinicalPhaseFilters'
  )
  const showClinicalPhase = getValue(
    pipelineFilters,
    'show_clinical_phase_filter',
    'showClinicalPhaseFilter'
  )
  const clinicalPhaseOptionRows =
    getValue(
      clinicalPhaseConfig,
      'clinical_phase_filter_options',
      'clinicalPhaseFilterOptions'
    ) || []

  clinicalPhaseOrder.value = mapOptionList(
    clinicalPhaseOptionRows,
    'clinical_phase_filter_option',
    'clinicalPhaseFilterOption'
  )
  clinicalPhaseFilters.value = showClinicalPhase ? clinicalPhaseOrder.value : []

  clinicalPhaseShortLabels.value = Array.isArray(clinicalPhaseOptionRows)
    ? clinicalPhaseOptionRows.reduce((map, row) => {
        const fullLabel = getValue(
          row,
          'clinical_phase_filter_option',
          'clinicalPhaseFilterOption'
        )
        const shortLabel = getValue(
          row,
          'clinical_phase_filter_short_label',
          'clinicalPhaseFilterShortLabel'
        )

        if (typeof fullLabel === 'string' && fullLabel.trim()) {
          map[fullLabel.trim()] =
            typeof shortLabel === 'string' && shortLabel.trim()
              ? shortLabel.trim()
              : fullLabel.trim()
        }

        return map
      }, {})
    : {}

  const treatmentApproachConfig = getValue(
    pipelineFilters,
    'treatment_approach_filters',
    'treatmentApproachFilters'
  )
  const showTreatmentApproach = getValue(
    pipelineFilters,
    'show_treatment_approach_filter',
    'showTreatmentApproachFilter'
  )
  treatmentApproachFilters.value = showTreatmentApproach
    ? mapOptionList(
        getValue(
          treatmentApproachConfig,
          'treatment_approach_filter_options',
          'treatmentApproachFilterOptions'
        ),
        'treatment_approach_filter_option',
        'treatmentApproachFilterOption'
      )
    : []

  const moleculeConfig = getValue(
    pipelineFilters,
    'molecule_filters',
    'moleculeFilters'
  )
  const showMolecule = getValue(
    pipelineFilters,
    'show_molecule_filter',
    'showMoleculeFilter'
  )
  moleculeFilters.value = showMolecule
    ? mapOptionList(
        getValue(
          moleculeConfig,
          'molecule_filter_options',
          'moleculeFilterOptions'
        ),
        'filter_option',
        'filterOption'
      )
    : []
}

const getFrontCardInformation = (pipeline) => {
  const clinicalTrial =
    getValue(pipeline, 'clinical_trial', 'clinicalTrial') || {}
  return (
    getValue(clinicalTrial, 'front_card_information', 'frontCardInformation') ||
    {}
  )
}

const getCardTagName = (pipeline) => {
  const frontCardInformation = getFrontCardInformation(pipeline)
  const tagName = getValue(frontCardInformation, 'tag_name', 'tagName')

  if (typeof tagName === 'string' && tagName.trim()) {
    return tagName.trim()
  }

  const diseaseAreas = getValue(
    frontCardInformation,
    'disease_areas',
    'diseaseAreas'
  )

  if (Array.isArray(diseaseAreas)) {
    return diseaseAreas.find(
      (diseaseArea) => typeof diseaseArea === 'string' && diseaseArea.trim()
    )
  }

  return typeof diseaseAreas === 'string' ? diseaseAreas.trim() : ''
}

const getClinicalPhaseGroupTitle = (pipeline) => {
  const frontCardInformation = getFrontCardInformation(pipeline)
  const clinicalPhaseValue = getValue(
    frontCardInformation,
    'clinical_phase',
    'clinicalPhase'
  )

  if (typeof clinicalPhaseValue === 'string' && clinicalPhaseValue.trim()) {
    return clinicalPhaseValue.trim()
  }

  return 'Other'
}

const getCardTitleDisplay = (titleValue) => {
  if (typeof titleValue !== 'string') {
    return titleValue
  }

  // Swap regular hyphens for a non-breaking hyphen so short codes like
  // "RMC-5127" stay on one line, while longer multi-word titles can still
  // wrap normally at spaces instead of overflowing the card.
  return titleValue.replace(/-/g, '‑')
}

const getClinicalPhaseCardLabel = (clinicalPhaseValue) => {
  if (typeof clinicalPhaseValue !== 'string' || !clinicalPhaseValue.trim()) {
    return clinicalPhaseValue
  }

  const trimmedValue = clinicalPhaseValue.trim()

  return clinicalPhaseShortLabels.value[trimmedValue] || trimmedValue
}

const filteredPipelineItems = computed(() => {
  const items = Array.isArray(props.data?.pipeline_items)
    ? props.data.pipeline_items
    : []

  return items.filter((pipeline) => {
    const frontCardInformation = getFrontCardInformation(pipeline)

    const diseaseAreasValue = getValue(
      frontCardInformation,
      'disease_areas',
      'diseaseAreas'
    )
    const clinicalPhaseValue = getValue(
      frontCardInformation,
      'clinical_phase',
      'clinicalPhase'
    )
    const treatmentApproachValue = getValue(
      frontCardInformation,
      'treatment_approach',
      'treatmentApproach'
    )
    const moleculeValue = getValue(frontCardInformation, 'molecule', 'molecule')

    const matchesDiseaseAreas = matchesSelectedFilters(
      selectedDiseaseAreas.value,
      diseaseAreasValue
    )
    const matchesClinicalPhase = matchesSelectedFilters(
      selectedClinicalPhases.value,
      clinicalPhaseValue
    )
    const matchesTreatmentApproach = matchesSelectedFilters(
      selectedTreatmentApproaches.value,
      treatmentApproachValue
    )
    const matchesMolecule = matchesSelectedFilters(
      selectedMolecules.value,
      moleculeValue
    )

    return (
      matchesDiseaseAreas &&
      matchesClinicalPhase &&
      matchesTreatmentApproach &&
      matchesMolecule
    )
  })
})

const groupedPipelineItems = computed(() => {
  const groupedMap = new Map()

  filteredPipelineItems.value.forEach((pipeline, flatIndex) => {
    const title = getClinicalPhaseGroupTitle(pipeline)

    if (!groupedMap.has(title)) {
      groupedMap.set(title, [])
    }

    groupedMap.get(title).push({ pipeline, flatIndex })
  })

  const phaseOrderMap = new Map(
    clinicalPhaseOrder.value.map((phase, index) => [
      normalizeComparableValue(phase),
      index,
    ])
  )

  return Array.from(groupedMap.entries())
    .map(([title, items], originalIndex) => ({
      title,
      items,
      originalIndex,
      order:
        phaseOrderMap.get(normalizeComparableValue(title)) ??
        Number.MAX_SAFE_INTEGER,
    }))
    .sort((firstGroup, secondGroup) => {
      if (firstGroup.order !== secondGroup.order) {
        return firstGroup.order - secondGroup.order
      }

      return firstGroup.originalIndex - secondGroup.originalIndex
    })
    .map(({ title, items }) => ({ title, items }))
})

const getPhaseDisclaimer = (phaseTitle) => {
  const phaseDisclaimers = props.data?.phase_disclaimers
  const match = Array.isArray(phaseDisclaimers)
    ? phaseDisclaimers.find((item) => item?.phase?.trim() === phaseTitle)
    : null

  return match?.disclaimer || ''
}

const getCardKey = (itemIndex) => `${itemIndex}`

const getProgressBarValue = (pipeline) => {
  const frontCardInformation = getFrontCardInformation(pipeline)
  const progressBar = getValue(
    frontCardInformation,
    'progress_bar',
    'progressBar'
  )
  const parsedProgress = Number(progressBar)

  if (Number.isNaN(parsedProgress)) {
    return 0
  }

  return Math.max(0, Math.min(100, parsedProgress))
}

const getProgressBarStyle = (pipeline) => ({
  width: `${getProgressBarValue(pipeline)}%`,
})

const isCardActive = (itemIndex) =>
  Boolean(activeCards.value[getCardKey(itemIndex)])

const toggleCardActive = async (itemIndex) => {
  const key = getCardKey(itemIndex)

  activeCards.value = {
    ...activeCards.value,
    [key]: !activeCards.value[key],
  }

  await setCardHeights()
}

const setCardHeights = async () => {
  await nextTick()

  const groupedContainers = moduleRootRef.value
    ? Array.from(moduleRootRef.value.querySelectorAll('.pipeline-group'))
    : []
  const GROUP_SIZE = 3
  const CARD_BOTTOM_OFFSET = 49

  const resetCardHeights = (cards) => {
    cards.forEach((card) => {
      const front = card.querySelector('.front-card')
      const back = card.querySelector('.back-card')
      const innerTop = card.querySelector('.inner-top')

      card.style.height = 'auto'
      if (front) front.style.height = 'auto'
      if (back) back.style.height = 'auto'
      if (innerTop) innerTop.style.height = 'auto'
    })
  }

  const applyHeightsByCard = (cards) => {
    resetCardHeights(cards)

    cards.forEach((card) => {
      const innerTop = card.querySelector('.inner-top')
      const innerTopHeight = innerTop ? innerTop.scrollHeight : 0

      if (innerTop) innerTop.style.height = `${innerTopHeight}px`
    })

    cards.forEach((card) => {
      const front = card.querySelector('.front-card')
      const back = card.querySelector('.back-card')
      const frontHeight = front ? front.scrollHeight : 0
      const backHeight = back ? back.scrollHeight : 0
      const cardContentHeight = Math.max(frontHeight, backHeight)

      card.style.height = `${cardContentHeight + CARD_BOTTOM_OFFSET}px`
      if (front) front.style.height = `${cardContentHeight}px`
      if (back) back.style.height = `${cardContentHeight}px`
    })
  }

  const applyHeightsByChunks = (cards) => {
    resetCardHeights(cards)

    const getChunkMaxContentHeight = (chunk) =>
      chunk.reduce((max, card) => {
        const front = card.querySelector('.front-card')
        const back = card.querySelector('.back-card')
        const frontHeight = front ? front.scrollHeight : 0
        const backHeight = back ? back.scrollHeight : 0
        return Math.max(max, frontHeight, backHeight)
      }, 0)

    const applyChunkContentHeight = (chunk, contentHeight) => {
      chunk.forEach((card) => {
        const front = card.querySelector('.front-card')
        const back = card.querySelector('.back-card')

        card.style.height = `${contentHeight + CARD_BOTTOM_OFFSET}px`
        if (front) front.style.height = `${contentHeight}px`
        if (back) back.style.height = `${contentHeight}px`
      })
    }

    for (let start = 0; start < cards.length; start += GROUP_SIZE) {
      const chunk = cards.slice(start, start + GROUP_SIZE)

      const chunkMaxInnerTopHeight = chunk.reduce((max, card) => {
        const innerTop = card.querySelector('.inner-top')
        const innerTopHeight = innerTop ? innerTop.scrollHeight : 0
        return Math.max(max, innerTopHeight)
      }, 0)

      chunk.forEach((card) => {
        const innerTop = card.querySelector('.inner-top')
        if (innerTop) innerTop.style.height = `${chunkMaxInnerTopHeight}px`
      })

      let chunkMaxContentHeight = getChunkMaxContentHeight(chunk)
      applyChunkContentHeight(chunk, chunkMaxContentHeight)

      // Run a second pass after heights are applied to avoid stale measurements.
      chunkMaxContentHeight = getChunkMaxContentHeight(chunk)
      applyChunkContentHeight(chunk, chunkMaxContentHeight)
    }
  }

  if (groupedContainers.length) {
    groupedContainers.forEach((groupContainer) => {
      const cards = Array.from(groupContainer.querySelectorAll('.item'))
      if (isDesktop.value) {
        applyHeightsByChunks(cards)
        return
      }

      applyHeightsByCard(cards)
    })
    return
  }

  const cards = Array.isArray(cardRefs.value)
    ? cardRefs.value
    : cardRefs.value
    ? [cardRefs.value]
    : []

  if (isDesktop.value) {
    applyHeightsByChunks(cards)
    return
  }

  applyHeightsByCard(cards)
}

const scheduleCardHeightsAfterLayout = () => {
  if (typeof window === 'undefined') {
    setCardHeights()
    return
  }

  if (layoutRefreshFrame) {
    window.cancelAnimationFrame(layoutRefreshFrame)
  }

  layoutRefreshFrame = window.requestAnimationFrame(() => {
    layoutRefreshFrame = window.requestAnimationFrame(async () => {
      layoutRefreshFrame = null

      if (!moduleRootRef.value) {
        return
      }

      await setCardHeights()
      locoScroll.value?.resize?.()
    })
  })
}

onBeforeMount(async () => {
  acf_options.value = await store.getAcfOptions()
  hydratePipelineFilters()
})

onMounted(() => {
  setCardHeights()
  scheduleCardHeightsAfterLayout()

  if (typeof document !== 'undefined' && document.fonts?.ready) {
    document.fonts.ready.then(() => {
      scheduleCardHeightsAfterLayout()
    })
  }

  if (typeof window !== 'undefined') {
    windowLoadHandler = () => {
      scheduleCardHeightsAfterLayout()
    }

    if (document.readyState === 'complete') {
      windowLoadHandler()
    } else {
      window.addEventListener('load', windowLoadHandler, { once: true })
    }
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    if (layoutRefreshFrame) {
      window.cancelAnimationFrame(layoutRefreshFrame)
      layoutRefreshFrame = null
    }

    if (windowLoadHandler) {
      window.removeEventListener('load', windowLoadHandler)
      windowLoadHandler = null
    }
  }

  if (filterLoadingTimer) {
    clearTimeout(filterLoadingTimer)
    filterLoadingTimer = null
  }
})

watch(
  device,
  async (currentDevice) => {
    filtersOpen.value = currentDevice === 'desktop'

    syncMobilePipelineGroupsOpen()
    await setCardHeights()
  },
  { immediate: true }
)

watch(
  groupedPipelineItems,
  () => {
    syncMobilePipelineGroupsOpen()
  },
  { deep: true, immediate: true }
)

watch(
  [isFiltering],
  async ([loading]) => {
    if (!loading) {
      await setCardHeights()
      await nextTick()

      if (locoScroll.value?.resize) {
        locoScroll.value.resize()
      }

      locoScroll.value?.start?.()
    }
  },
  { deep: true }
)

watch(
  [
    selectedDiseaseAreas,
    selectedClinicalPhases,
    selectedTreatmentApproaches,
    selectedMolecules,
    filteredPipelineItems,
  ],
  async () => {
    activeCards.value = {}
    await setCardHeights()
  },
  { deep: true }
)
</script>

<style lang="scss" scoped>
.pipeline-with-filters-module-component {
  @apply w-full max-w-screen-xl relative z-3 mx-auto py-8 lg:py-10 px-6 lg:px-0;
  overflow-anchor: none;

  .pipeline-container {
    @apply flex flex-col gap-8;

    .title-section {
      @apply flex items-center justify-between gap-3;

      .title-section-heading {
        @apply header-3 font-semibold m-0;
      }
    }

    .pipeline {
      .disclaimer-div {
        @apply mt-10 lg:mt-[75px];

        :deep(*) {
          @apply body;
        }
      }

      .filters {
        @apply bg-white/50 rounded-[10px] p-6 mb-10;
        @apply lg:mb-[75px];

        .filter-trigger {
          @apply w-full flex justify-between items-center gap-2;

          .filter-header {
            @apply flex justify-between items-center gap-3 w-full lg:justify-start;

            .clear-all {
              @apply text-[16px] font-medium bg-transparent w-fit text-balanced underline underline-offset-2 transition-opacity;
              @apply hover:opacity-70;
            }
          }

          p {
            @apply header-4 m-0;
          }

          button {
            @apply bg-white w-[35px] h-[35px] flex justify-center items-center rounded-full relative z-[3];
          }
        }

        .filters-dropdown {
          @apply mt-6 flex flex-col gap-4;
          @apply lg:gap-10 lg:flex-row lg:flex-wrap;

          .filter-field {
            @apply flex flex-col gap-2 lg:flex-1;

            .filter-mobile-trigger {
              @apply w-full flex items-center justify-between gap-3 text-left pb-1 border-b border-balanced;
              @apply lg:border-none;

              svg {
                @apply transition-transform duration-300 rotate-180 lg:hidden;
                @apply w-[23px] h-[23px] p-[4px] border border-balanced rounded-full;
              }

              &.open {
                svg {
                  @apply rotate-0;
                }
              }
            }

            .filter-label {
              @apply relative w-full pb-1 text-[16px] text-humble;

              &:before {
                @apply content-[''] hidden lg:block absolute bottom-0 left-0 w-full h-[1px] bg-humble;
              }
            }

            .button-list {
              @apply flex flex-wrap gap-3 mt-5 mb-10 lg:mb-0;

              .filter-button {
                @apply rounded-[16px] bg-functional px-[15px] py-2 text-[14px] leading-[1.2] transition-colors;

                &.active {
                  @apply bg-balanced text-white;
                }
              }
            }

            &.molecule-filter {
              .button-list .filter-button {
                :deep(sub) {
                  bottom: auto;
                  top: -0.5em;
                  font-size: 9px;
                  color: inherit !important;
                }
              }
            }
          }
        }
      }

      .pipeline-group {
        @apply w-full;

        + .pipeline-group {
          @apply mt-12 lg:mt-[75px];
        }

        .group-header {
          @apply flex items-center justify-between gap-3 mb-4 pb-2 border-b border-black lg:pb-5 lg:mb-[50px];

          .group-title {
            @apply header-3 font-semibold m-0;
          }

          .group-mobile-trigger {
            @apply flex-[1_0_auto] max-w-[35px] h-[35px] w-[35px] flex justify-center items-center rounded-full border border-balanced;

            svg {
              @apply transition-transform duration-500 rotate-180;
            }

            &.open {
              svg {
                @apply rotate-0;
              }
            }
          }
        }

        .phase-disclaimer {
          @apply mt-6 lg:mt-10;

          :deep(*) {
            @apply body;
          }
        }

        .pipeline-items {
          @apply flex flex-col gap-5 lg:flex-row lg:flex-wrap;
        }

        &.skeleton-group {
          .group-title.skeleton-title {
            @apply bg-functional h-[28px] w-[240px] rounded-[8px];
          }

          .skeleton-item {
            .front-card {
              .top {
                @apply flex flex-col gap-3;
              }

              .skeleton-line {
                @apply bg-functional rounded-[8px] h-[14px];

                &.skeleton-line-lg {
                  @apply w-[80%] h-[18px];
                }

                &.skeleton-line-md {
                  @apply w-[65%];
                }

                &.skeleton-line-sm {
                  @apply w-[50%];
                }
              }

              .skeleton-pill {
                @apply bg-functional rounded-[10px] h-[28px] w-[120px];
              }

              .skeleton-circle {
                @apply bg-functional rounded-full h-[35px] w-[35px];
              }
            }
          }
        }

        .top-title {
          :deep(*) {
            @apply header-3 font-semibold mb-4;
          }
        }

        .item {
          @apply bg-white rounded-b-[10px] rounded-tr-[40px] rounded-tl-[10px] p-6 overflow-hidden;
          border-radius: 10px 40px 10px 10px;
          background: var(--White, #fff);
          box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.25);

          transition: border-radius 0.5s ease;
          @apply w-full lg:w-[calc(33.333%-14px)];

          .pipeline-button {
            @apply bg-progressive pointer-events-none text-balanced px-3 py-[6px] rounded-[10px] text-[16px] absolute z-2 top-0 left-0 opacity-0 transition-all;
            @apply hover:bg-balanced hover:text-white;
          }

          .inner-item {
            .front-card {
              @apply flex flex-col justify-between h-full;

              .small-title {
                @apply text-[12px] text-[#737373];
              }

              .top {
                @apply mb-4;

                .title-type {
                  @apply flex justify-between items-start gap-4 mb-2 opacity-100 transition-all duration-500;
                  animation: show-title 0.5s forwards;

                  .title {
                    @apply text-[20px] xl:header-4 font-semibold;

                    :deep(sup) {
                      font-size: 10px;
                      top: -1.3em;
                    }
                  }

                  .focus {
                    @apply px-2 py-1 rounded-[10px] w-[140px] bg-[rgba(209,166,255,1)] text-sm text-white;

                    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.15);

                    :deep(*) {
                      @apply m-0 text-[12px] font-medium text-center;
                    }
                  }
                }

                .subtitle-logo {
                  @apply flex-1 min-w-0 flex flex-col justify-between items-start gap-4 opacity-100 transition-all duration-500;
                  animation: show-title 0.5s forwards;

                  .subtitle {
                  }

                  img {
                    @apply w-full object-contain max-w-[140px];
                  }

                  &.down {
                    @apply w-auto max-w-[140px] flex-none items-end;
                  }
                }

                .details {
                  @apply my-4 opacity-100 transition-all;
                  animation: show-title-down 0.5s forwards;

                  .details-title {
                    @apply text-[#A3A3A3] uppercase mb-1;
                  }

                  .details-content {
                    @apply body;
                  }
                }
              }

              .bottom {
                @apply flex items-center gap-3 relative justify-between transition-all duration-500;

                .trigger {
                  @apply flex-[1_0_auto] max-w-[35px] h-[35px] w-[35px] flex justify-center items-center rounded-full border border-balanced relative z-[3];
                }

                span {
                  @apply text-[10px] flex text-humble m-0 leading-[1];
                }

                .title-tag {
                  @apply w-full flex max-w-[150px] ml-0 relative rounded-full h-[22px] overflow-hidden;
                  background: #ffff;
                  border: 2px solid #0e4343;

                  .background-tag {
                    @apply absolute top-0 left-0 h-full rounded-full;
                    background: #1e5b4e;
                    transition: width 1s ease;
                  }

                  &.is-complete {
                    background: #1e5b4e;

                    .background-tag {
                      background: none;
                    }
                  }
                }

                .phase-label {
                  @apply text-[12px] tracking-[-0.3px] p-[10px] rounded-[10px] bg-functional leading-[1.2] m-0;
                }
              }
            }

            .back-card {
              @apply transition-all opacity-0 duration-1000 z-2 flex flex-col;
              animation: back-down 0.4s forwards;

              .details-content {
                :deep(*) {
                  @apply text-[16px] leading-[22px] mb-3;

                  strong {
                    @apply text-balanced;
                  }
                }
              }

              .disclaimer {
                @apply flex flex-col;

                :deep(*) {
                  @apply text-[12px] text-gray_green leading-[1.2] mb-4;
                }
              }
            }
          }

          &.active {
            @apply rounded-tr-[10px] rounded-tl-[40px];

            .back-card {
              animation: back-up 0.5s forwards;
            }

            .front-card {
              .top {
                .title-type {
                  animation: hide-title 0.3s forwards 0.1s;
                }

                .subtitle-logo {
                  animation: hide-title 0.3s forwards 0.2s;
                }

                .details {
                  animation: hide-up-title 0.3s forwards 0s;
                }
              }

              .bottom {
                .title-tag,
                .clinical-phase-value,
                .phase-label {
                  animation: hide-title 0.3s forwards 0s;
                }

                .pipeline-button {
                  @apply opacity-100 pointer-events-auto;
                }

                .trigger {
                  svg {
                    @apply rotate-180 transition-transform duration-500;
                  }
                }
              }
            }
          }
        }

        &.pair {
          .item {
            .inner-item {
              .front-card {
                .bottom {
                  .title-tag {
                    span {
                      @apply text-white;
                    }

                    &:after {
                      @apply left-0 right-auto bg-progressive;
                    }
                  }
                }
              }
            }
          }
        }
      }

      .no-results {
        @apply w-full rounded-[10px] bg-white p-6;

        p {
          @apply m-0 text-[16px] text-[#404040];
        }
      }
    }
  }

  @keyframes back-down {
    0% {
      transform: translateY(-100%);
      opacity: 1;
    }

    60% {
      opacity: 0.4;
    }

    100% {
      transform: translateY(0%);
      opacity: 0;
    }
  }

  @keyframes back-up {
    0% {
      transform: translateY(0%);
      opacity: 0;
    }

    60% {
      opacity: 0.4;
    }

    100% {
      transform: translateY(-100%);
      opacity: 1;
    }
  }

  @keyframes hide-title {
    0% {
      opacity: 1;
    }

    100% {
      opacity: 0;
    }
  }

  @keyframes show-title {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  @keyframes hide-up-title {
    0% {
      transform: translateY(0%);
      opacity: 1;
    }

    100% {
      transform: translateY(-10px);
      opacity: 0;
    }
  }

  @keyframes show-title-down {
    0% {
      transform: translateY(-10px);
      opacity: 0;
    }

    100% {
      transform: translateY(0%);
      opacity: 1;
    }
  }
}
</style>
